export function formatDomain(url) {
    try {
        // Add a default protocol if missing
        if (!/^https?:\/\//i.test(url)) {
            url = 'http://' + url;
        }
        // Use encodeURI to handle special characters in the URL
        let hostname = new URL(encodeURI(url)).hostname; // Extract domain from full URL
        return hostname.replace(/^www\./, ''); // Remove 'www.' if present
    } catch {
        return url.toLowerCase(); // Fallback for invalid URLs
    }
}

// Builds a readable summary of the provider's office county locations.
function formatOfficeLocations(officeLocations) {
    const counties = Array.isArray(officeLocations) ? officeLocations.filter(Boolean) : []
    if (counties.length === 0) return 'Office Location'
    return `${counties.length === 1 ? 'Office' : 'Offices'} in ${counties.join(', ')}`
}

export function getBulletIcons(profileDetails) {
    const bulletIcons = {
        yearEstablished: {
            icon: 'fa-calendar',
            text: 'Established in ' + profileDetails.yearEstablished
        },
        panelWarranty: {
            icon: 'fa-bolt',
            text: profileDetails.panelWarranty + ' Solar Panel Warranty'
        },
        performanceWarranty: {
            icon: 'fa-chart-line',
            text: profileDetails.performanceWarranty + ' Performance Warranty'
        },
        insulationWarranty: {
            icon: 'fa-bolt',
            text: profileDetails.insulationWarranty + ' Insulation Warranty'
        },
        evChargerWarranty: {
            icon: 'fa-bolt',
            text: profileDetails.evChargerWarranty + ' EV Charger Warranty'
        },
        heatPumpWarranty: {
            icon: 'fa-bolt',
            text: profileDetails.heatPumpWarranty + ' Heat Pump Warranty'
        },
        workmanshipWarranty: {
            icon: 'fa-wrench',
            text: profileDetails.workmanshipWarranty + ' Workmanship Warranty'
        },
        totalStaff: {
            icon: 'fa-users',
            text: profileDetails.totalStaff + ' Staff'
        },
        totalInstallations: {
            icon: 'fa-check-double',
            text: profileDetails.totalInstallations + ' Total Installations'
        },
        annualInstallations: {
            icon: 'fa-check',
            text: profileDetails.annualInstallations + ' Annual Installations'
        },
        officeLocations: {
            icon: 'fa-map-marker',
            text: formatOfficeLocations(profileDetails.officeLocations)
        },
        publicLiabilityInsurance: {
            icon: 'fa-calculator',
            text: 'Public Liability Insurance'
        },
        professionalIndemnityInsurance: {
            icon: 'fa-file-alt',
            text: 'Professional Indemnity Insurance'
        },
        dedicatedSupport: {
            icon: 'fa-phone',
            text: 'Dedicated Support Team'
        },
        seaiApproved: {
            icon: 'fa-thumbs-up',
            text: 'SEAI Approved Installer'
        }
    }
    return bulletIcons
}

// Resolves the selected bullets into an ordered list of { key, icon, text } items
// ready to render. Each product warranty is its own selectable bullet drawn from
// the matching Public Profile field; bullets whose text would render as
// "undefined ..." (missing underlying value) are skipped.
export function resolveBullets(profileDetails, technology, bullets) {
    const selection = bullets || profileDetails.bullets || {}
    const icons = getBulletIcons(profileDetails)
    const items = []

    Object.keys(icons).forEach(key => {
        if (!selection[key]) return
        if (String(icons[key].text).includes('undefined')) return
        items.push({ key, icon: icons[key].icon, text: icons[key].text })
    })

    return items
}

export function checkPrimaryPersonCompletion(company) {
    const fields = ['firstName', 'lastName', 'email', 'phone'];

    if (!company?.primaryPerson) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.primaryPerson[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkCompanyProfileCompletion(company) {
    const isSolar = !!company?.productsAvailable?.['Solar'];
    const fields = ['logo', 'yearEstablished', 'workmanshipWarranty', 'totalStaff', 'officeLocations', 'dedicatedSupport', 'seaiApproved', 'publicLiabilityInsurance'];
    if (isSolar) {
        fields.push('annualInstallations', 'panelWarranty');
    }

    if (!company?.profileDetails) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.profileDetails[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkRecommendationCardCompletion(company) {
    if (!company?.profileDetails?.bullets) return 0;

    let filledCount = 0
    for (let key in company.profileDetails.bullets) {
        if (company.profileDetails.bullets[key]) {
            filledCount++
        }
    }
    console.log('filled count: ', filledCount)

    const completionPercentage = (filledCount / 6) * 100;
    return Math.round(completionPercentage);
}

export function checkAboutCompanyCompletion(company) {
    const fields = ['about', 'customerJourney', 'whyChoose'];

    if (!company?.profileDetailsExtended) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.profileDetailsExtended[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkCountiesCompletion(company) {
    if (company?.counties?.length > 0 || company?.eircodeAreas?.length > 0) {
        return 100;
    }
    return 0;
}

export function checkProjectsCompletion(company) {
    if (company?.projects?.length > 0) {
        return 100;
    }
    return 0;
}

export function checkPlanCompletion(company) {
    const fields = ['plan'];

    if (!company?.planDetails) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.planDetails[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkSurveyCompletion(company) {
    if (company?.surveyDetails && Object.keys(company.surveyDetails).length > 0) {
        return 100;
    }
    return 0;
}

export function checkCompanyVerificationCompletion(company) {
    const fields = ['registrationCert'];

    if (!company?.verification) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.verification[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkWebsiteVerificationCompletion(company) {
    return company.badgeVerified ? 100 : 0
}

export function checkPaymentCompletion(company) {
    const fields = ['paymentMethod'];

    if (!company?.paymentDetails) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.paymentDetails[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkDeliveryCompletion(company) {
    const fields = ['deliveryMethod', 'email'];

    if (!company?.deliveryDetails) return 0;

    let filledCount = 0;

    for (const field of fields) {
        // console.log('Checking field:', field);
        // console.log('Value:', company?.primaryPerson?.[field]);

        if (company.deliveryDetails[field]) {
            filledCount++;
        }
    }

    const completionPercentage = (filledCount / fields.length) * 100;
    return Math.round(completionPercentage);
}

export function checkTechnologyCompletion(company) {
    if (company?.technology && Object.keys(company.technology).length > 0) {
        return 100;
    }
    return 0;
}

export function getVideoEmbedUrl(url) {
    if (url.includes('youtube.com')) {
        const videoId = new URL(url).searchParams.get('v');
        return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be')) {
        const videoId = url.split('/').pop();
        return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('vimeo.com')) {
        const videoId = url.split('/').pop();
        return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
}

export function cleanQuillHtml(html) {
  return html
    .replace(/<p><br><\/p>/g, '') // remove empty lines
    .replace(/<p>\s*<\/p>/g, ''); // remove paragraphs with only whitespace
}

export function stringToHex(str) {
	let hex = ''
	for (let i = 0; i < str.length; i++) {
	  	const charCode = str.charCodeAt(i)
	  	const hexValue = charCode.toString(16)
	  	// Pad with zeros to ensure two-digit representation
	  	hex += hexValue.padStart(2, '0')
	}
	return hex
}

export function hexToString(hex) {
	let str = ''
  	for (let i = 0; i < hex.length; i += 2) {
    	const hexValue = hex.substr(i, 2)
    	const decimalValue = parseInt(hexValue, 16)
    	str += String.fromCharCode(decimalValue)
  	}
  	return str
}